import { motion } from "framer-motion";
import { Code2, Network, Wrench, LineChart, ShieldCheck, Gauge, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow, ease } from "./Reveal";

const services = [
  { key: "software", icon: Code2, title: "Aviation Software Solutions", text: "Purpose-built software for flight operations, maintenance and mission planning — designed around the people who use it.", img: "/img/cockpit.jpg", span: "md:col-span-7 md:row-span-2", tone: "photo" },
  { key: "integration", icon: Network, title: "Capability Integration", text: "Connecting platforms, systems and data so new capability lands cleanly alongside what you already fly.", span: "md:col-span-5", tone: "sky" },
  { key: "engineering", icon: Wrench, title: "In-house Engineering & Technical Support", text: "Hands-on engineering expertise across the lifecycle — from requirements to sustainment.", img: "/img/hangar.jpg", span: "md:col-span-5", tone: "photo" },
  { key: "data", icon: LineChart, title: "Data & Insight", text: "Turning operational data into decisions you can act on, quickly.", span: "md:col-span-4", tone: "light" },
  { key: "assurance", icon: ShieldCheck, title: "Systems Assurance", text: "Rigorous verification and documentation that keeps capability safe and compliant.", span: "md:col-span-4", tone: "light" },
  { key: "speed", icon: Gauge, title: "Rapid Delivery", text: "Lean, agile teams that ship at the speed your capability needs demand.", span: "md:col-span-4", tone: "ink" },
];

const tones = {
  sky: "bg-sky-600 text-white",
  light: "bg-white text-ink border border-sky-100",
  ink: "bg-ink text-white",
  photo: "text-white",
};

const Card = ({ s, i }) => {
  const Icon = s.icon;
  const dark = s.tone !== "light";
  return (
    <motion.article
      initial={{ opacity: 0.001, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay: (i % 3) * 0.1, ease }}
      whileHover={{ y: -6 }}
      className={`group relative flex min-h-[280px] flex-col justify-between overflow-hidden rounded-[28px] p-7 sm:p-8 ${tones[s.tone]} ${s.span}`}
      data-testid={`service-card-${s.key}`}
    >
      {s.img && (
        <>
          <img src={s.img} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#06223f]/90 via-[#06223f]/45 to-[#06223f]/10" />
        </>
      )}
      <div className="relative flex items-start justify-between">
        <span className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${dark ? "bg-white/15 backdrop-blur" : "bg-sky-50 text-sky-700"}`}>
          <Icon className="h-5 w-5" />
        </span>
        <span className={`font-mono text-xs tracking-[0.2em] ${dark ? "text-white/80" : "text-slate-500"}`}>0{i + 1}</span>
      </div>
      <div className="relative mt-10">
        <h3 className="font-display text-2xl font-semibold tracking-tight sm:text-[1.7rem]">{s.title}</h3>
        <p className={`mt-3 max-w-md text-sm leading-relaxed sm:text-base ${dark ? "text-white/85" : "text-slate-600"}`}>{s.text}</p>
        <ArrowUpRight className="absolute bottom-0 right-0 h-5 w-5 opacity-0 transition-[opacity,transform] duration-500 group-hover:-translate-y-1 group-hover:opacity-100" />
      </div>
    </motion.article>
  );
};

export const Services = () => (
  <section id="services" className="relative bg-[#F4F9FD] py-24 lg:py-32" data-testid="services-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-12">
      <div className="mb-14 grid gap-6 lg:grid-cols-12 lg:items-end">
        <Reveal className="lg:col-span-7">
          <Eyebrow>01 — Services</Eyebrow>
          <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-[-0.03em] text-ink sm:text-5xl">
            Capability, delivered <span className="font-serif font-normal italic text-sky-600">without</span> the drag.
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-5">
          <p className="text-base leading-relaxed text-slate-600 md:text-lg">
            Six disciplines, one outcome: aviation capability that arrives when it's needed, not years after.
          </p>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 gap-5 md:auto-rows-[300px] md:grid-cols-12 lg:gap-6" data-testid="services-bento-grid">
        {services.map((s, i) => (
          <Card key={s.key} s={s} i={i} />
        ))}
      </div>
    </div>
  </section>
);
